import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import { withStyles, Typography } from '@material-ui/core';

import { ForwardIcon } from '../../../common/icons';
import urls from '../../../app/urls';
import { Image } from '../../../youwell-common/components';

import flagImage from '../../../common/image/icon_flag.png';
import contentImage from '../../../common/image/icon_contents.png';
import kiteImage from '../../../common/image/icon_kite.png';

const styles = theme => ({
    root: {
        paddingBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            paddingBottom: theme.spacing(1),
        },
    },
    infoBox: {
        flexBasis: `calc(100% / 4 - ${theme.spacing(2)}px)`,
        margin: theme.spacing(1),
        background: theme.colors.grey2,
        position: 'relative',

        [theme.breakpoints.down('xs')]: {
            margin: theme.spacing(0, 0, 1, 0),
        },
    },
    header: {
        padding: theme.spacing(1, 2),
        background: theme.colors.blue4,
        color: theme.palette.common.white,
    },
    links: {
        padding: theme.spacing(2),
    },
    link: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),

        [theme.breakpoints.down('xs')]: {
            justifyContent: 'space-between',
        },
        '&$disabled': {
            color: theme.palette.text.secondary,
            cursor: 'initial',
        },
    },
    linksImage: {
        position: 'absolute',
        bottom: theme.spacing(1),
        right: theme.spacing(1),
        width: 75,

        [theme.breakpoints.down('sm')]: {
            position: 'static',
        },
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    linksImageTop: {
        position: 'absolute',
        top: -16,
        right: -20,
        width: 90,

        [theme.breakpoints.down('sm')]: {
            position: 'static',
        },
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    underline: {
        borderBottom: '1px solid',
    },
    icon: {
        marginLeft: theme.spacing(1),
    },
    disabled: {

    },
});

const ContentBox = ({ classes, module, image }) => module && (
    <div className={classes.infoBox}>
        <Typography variant="caption" className={classes.header} display="block">
            {module.name}
        </Typography>
        <div className={classes.links}>
            {module.tasks?.map(task => (
                <Link key={task.id} to={`${urls.inside.page}/${task.id}`}>
                    <Typography variant="body2" className={classes.link}>
                        {task.name}
                        <ForwardIcon className={classes.icon} />
                    </Typography>
                </Link>
            ))}
        </div>
        {image && <Image src={image} className={classes.linksImage} />}
    </div>
);

ContentBox.propTypes = {
    classes: PropTypes.object.isRequired,
    module: PropTypes.object,
    image: PropTypes.string,
};

ContentBox.defaultProps = {
    module: null,
    image: null,
};

const ProgramInformation = ({
    className, classes, translate, methodModule, teachingModule, formsModule, resourceProgram, hasLicense,
// eslint-disable-next-line arrow-body-style
}) => {
    return (
        <div className={classnames(classes.root, className)}>
            <div className={classes.infoBox}>
                <Typography variant="caption" className={classes.header} display="block">
                    {translate('dashboard.aboutTheProgramHeader')}
                </Typography>
                <div className={classes.links}>
                    {formsModule?.tasks.map(form => (
                        <Link key={form.id} to={hasLicense ? `${urls.inside.form}/${form.id}` : '#'}>
                            <Typography variant="body2" className={classnames(classes.link, { [classes.disabled]: !hasLicense })}>
                                {form.name}
                                <ForwardIcon className={classes.icon} />
                            </Typography>
                        </Link>
                    ))}
                    <Link to={hasLicense ? urls.inside.program.home : '#'}>
                        <Typography variant="body2" className={classnames(classes.link, { [classes.disabled]: !hasLicense })}>
                            {translate('dashboard.70skills')}
                            <ForwardIcon className={classes.icon} />
                        </Typography>
                    </Link>
                </div>
            </div>

            <ContentBox
                classes={classes}
                module={methodModule}
                image={flagImage}
            />

            <ContentBox
                classes={classes}
                module={teachingModule}
                image={contentImage}
            />

            <div className={classes.infoBox}>
                <Typography variant="caption" className={classes.header} display="block">
                    {translate('dashboard.resourcesHeader')}
                </Typography>
                <div className={classes.links}>
                    {resourceProgram?.modules?.map(m => (
                        <Link key={m.id} to={`${urls.inside.resource.module}/${m.id}`}>
                            <Typography variant="body2" className={classes.link}>
                                {m.name}
                                <ForwardIcon className={classes.icon} />
                            </Typography>
                        </Link>
                    ))}
                </div>
                <Image src={kiteImage} className={classes.linksImageTop} />
            </div>
        </div>
    );
};

ProgramInformation.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    methodModule: PropTypes.object,
    teachingModule: PropTypes.object,
    resourceProgram: PropTypes.object,
    formsModule: PropTypes.object,
    className: PropTypes.string,
    hasLicense: PropTypes.bool,
};

ProgramInformation.defaultProps = {
    methodModule: null,
    teachingModule: null,
    resourceProgram: null,
    formsModule: null,
    className: null,
    hasLicense: false,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    methodModule: state.program.content && state.program.content[state.pageSettings.appConfig?.contentProgramId]?.modules?.find(d => d.id === state.pageSettings.appConfig?.methodModuleId),
    teachingModule: state.program.content && state.program.content[state.pageSettings.appConfig?.contentProgramId]?.modules?.find(d => d.id === state.pageSettings.appConfig?.teachingModuleId),
    resourceProgram: state.program.content && state.program.content[state.pageSettings.appConfig?.resourcesProgramId],
    formsModule: state.program.content && state.program.content[state.pageSettings.appConfig?.contentProgramId]?.modules?.find(d => d.id === state.pageSettings.appConfig?.formsModuleId),
});

export default connect(mapStateToProps)(withStyles(styles)(ProgramInformation));
